/**
 * KioskInactivityWarningModal.tsx
 *
 * Audible 15-second countdown warning modal when user inactivity is detected.
 * Provides explicit choices: [Continue Shopping] or [Start Over & Wipe Memory].
 */

import React from 'react';

export interface KioskInactivityWarningModalProps {
  readonly remainingSeconds: number;
  readonly onContinue: () => void;
  readonly onResetNow: () => void;
}

export const KioskInactivityWarningModal: React.FC<KioskInactivityWarningModalProps> = ({
  remainingSeconds,
  onContinue,
  onResetNow,
}) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        color: '#fff',
      }}
    >
      <div
        style={{
          backgroundColor: '#0f172a',
          borderRadius: 20,
          padding: 32,
          width: 440,
          maxWidth: '90vw',
          textAlign: 'center',
          border: '2px solid #eab308',
          boxShadow: '0 25px 50px -12px rgba(234, 179, 8, 0.3)',
        }}
      >
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>⏰</div>
        <h2 style={{ fontSize: '1.6rem', margin: '0 0 8px 0', fontWeight: 800 }}>
          Are you still there?
        </h2>
        <p style={{ color: '#94a3b8', fontSize: '1rem', margin: '0 0 20px 0' }}>
          Your session will reset to protect your privacy in:
        </p>

        {/* Countdown Circle / Badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 70,
            height: 70,
            borderRadius: '50%',
            backgroundColor: '#854d0e',
            color: '#fef08a',
            fontSize: '2rem',
            fontWeight: 900,
            marginBottom: 24,
          }}
        >
          {remainingSeconds}s
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            onClick={onContinue}
            style={{
              padding: '16px 0',
              fontSize: '1.15rem',
              fontWeight: 800,
              backgroundColor: '#10b981',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              cursor: 'pointer',
            }}
          >
            ✅ Yes, Continue Shopping
          </button>
          <button
            type="button"
            onClick={onResetNow}
            style={{
              padding: '12px 0',
              fontSize: '0.95rem',
              fontWeight: 600,
              backgroundColor: 'transparent',
              color: '#94a3b8',
              border: '1px solid #334155',
              borderRadius: 12,
              cursor: 'pointer',
            }}
          >
            ❌ No, Start Over
          </button>
        </div>
      </div>
    </div>
  );
};
